import { useLocale, useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { ElasticListVehicle } from '@//app/api/search/vehicles';
import type { ProductCardPriceProps } from '@//components/ProductCard';
import type { Locale } from '@//config/domain-locale-map';
import { formatCurrency } from '@//utils/currency';

interface UseProductCardContainerPricesOptions {
  currency: string;
}

interface UseProductCardContainerPricesReturn {
  mainPrice: ProductCardPriceProps;
  otherPrices: ProductCardPriceProps[];
}

const useProductCardContainerPrices = (
  item: ElasticListVehicle,
  { currency }: UseProductCardContainerPricesOptions,
): UseProductCardContainerPricesReturn => {
  const locale = useLocale() as Locale;
  const tVehicleGlossary = useTranslations('utils.vehicleGlossary');

  // Determine which price to use as mainPrice with fallback logic
  const mainPriceInfo = useMemo(() => {
    const hasCashPrice = item.price && item.price > 0;

    if (hasCashPrice) {
      return {
        label: tVehicleGlossary('price.cash'),
        value: formatCurrency(item.price, { currency, locale }),
        usedPrice: 'cash' as const,
      };
    }

    // Fallback to leasingPrice if cash price is unavailable
    if (item.leasingPrice && item.leasingPrice > 0) {
      return {
        label: tVehicleGlossary('price.privateLease.label'),
        value: formatCurrency(item.leasingPrice, { currency, locale, period: 'month' }),
        usedPrice: 'leasing' as const,
      };
    }

    // Fallback to businessLeasingPrice if both cash and leasing prices are unavailable
    if (item.businessLeasingPrice && item.businessLeasingPrice > 0) {
      return {
        label: tVehicleGlossary('price.businessLease.label'),
        value: formatCurrency(item.businessLeasingPrice, { currency, locale, period: 'month' }),
        usedPrice: 'businessLeasing' as const,
      };
    }

    // Fallback to showing cash price even if it's 0 or undefined
    return {
      label: tVehicleGlossary('price.cash'),
      value: formatCurrency(item.price, { currency, locale }),
      usedPrice: 'cash' as const,
    };
  }, [
    item.price,
    item.leasingPrice,
    item.businessLeasingPrice,
    currency,
    locale,
    tVehicleGlossary,
  ]);

  // Build otherPrices array, excluding the price used as mainPrice
  const otherPrices = useMemo(() => {
    const prices: ProductCardPriceProps[] = [];

    if (item.leasingPrice && item.leasingPrice > 0 && mainPriceInfo.usedPrice !== 'leasing') {
      prices.push({
        label: tVehicleGlossary('price.privateLease.label'),
        value: formatCurrency(item.leasingPrice, {
          currency,
          locale,
          period: 'month',
        }),
      });
    }

    if (
      item.businessLeasingPrice &&
      item.businessLeasingPrice > 0 &&
      mainPriceInfo.usedPrice !== 'businessLeasing'
    ) {
      prices.push({
        label: tVehicleGlossary('price.businessLease.label'),
        value: formatCurrency(item.businessLeasingPrice, {
          currency,
          locale,
          period: 'month',
        }),
      });
    }

    return prices;
  }, [
    item.leasingPrice,
    item.businessLeasingPrice,
    mainPriceInfo.usedPrice,
    currency,
    locale,
    tVehicleGlossary,
  ]);

  return {
    mainPrice: {
      label: mainPriceInfo.label,
      value: mainPriceInfo.value,
    },
    otherPrices,
  };
};

export default useProductCardContainerPrices;
